import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createCoachProfile, createUser, getUsers } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { AllTimeZones, findTimeZoneByValue } from '@/lib/time-zones';
import { CreateUserCommonForm } from '@/routes/_layout/users/-components/create-user-common-form';
import {
  createUserCommonFormSchema,
  createUserOptionsFromFormData,
} from '@/routes/_layout/users/-components/form-schema';
import { coach } from '@airreading/common';
import { UpdateCoachOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface CreateCoachProps {
  trigger?: ReactNode;
  fromUser?: UserWithRolesAndProfiles;
}

const formSchema = createUserCommonFormSchema.extend({
  isTrial: z.boolean().default(true),
});
type Form = z.infer<typeof formSchema>;

export const CreateCoach = ({ trigger, fromUser }: CreateCoachProps) => {
  const { mutateAsync: createCoachProfileMutate } = useMutation({
    mutationFn: createCoachProfile,
  });

  const { mutateAsync: createUserMutate } = useMutation({
    mutationFn: createUser,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = ({ formData, closeModal }: SubmitProps<Form>) => {
    try {
      const createUserPayload = createUserOptionsFromFormData(formData);
      const { isTrial } = formData;

      const coachProfilePayload: UpdateCoachOptions = {
        isTrial,
      };

      const apiCall = fromUser
        ? () => createCoachProfileMutate({ userId: fromUser.id, data: coachProfilePayload })
        : () =>
            createUserMutate({
              ...createUserPayload,
              assignRoles: [coach()],
              coachProfile: coachProfilePayload,
            });

      return apiCall()
        .then(() => {
          toast({
            title: 'Teacher created successfully',
          });
          closeModal();
          queryClient.invalidateQueries(getUsers());
          fromUser && queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(fromUser.id) });
        })
        .catch((error) => {
          const airServerError = airServerErrorFrom(error);
          const errorMessage = airServerError?.userFacingMessage || 'Teacher creation failed';

          toast({
            title: 'Teacher creation failed',
            description: errorMessage,
          });
        });
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        toast({ title: error.message });
        return;
      }
    }
  };

  return (
    <FormModal
      modalTitle="Create teacher"
      dialogContentProps={{ className: 'max-w-3xl' }}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Create teacher</Button>}
      defaultValues={{
        isTrial: true,
        ...(fromUser
          ? {
              firstName: fromUser.firstName || undefined,
              lastName: fromUser.lastName || undefined,
              email: fromUser.email || undefined,
              phone: fromUser.phone || undefined,
              timeZone: findTimeZoneByValue(fromUser.timeZone) || AllTimeZones[0],
              edlinkId: undefined,
              iss: fromUser.externalKeys?.iss || undefined,
              sub: fromUser.externalKeys?.sub || undefined,
              isRegistrable: fromUser.isRegistrable,
            }
          : {}),
      }}
    >
      {({ form }) => {
        return (
          <div className="grid grid-cols-2 gap-2 py-4">
            <CreateUserCommonForm form={form} disabled={!!fromUser} />
            <FormCheckbox name="isTrial" label="Is trial" form={form} />
          </div>
        );
      }}
    </FormModal>
  );
};
